import { useFlag } from '@calendly/feature-check-react';
import { I18n } from '@calendly/i18n';
import { BareButton } from '@calendly/ui/components/bare-button';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { FixedContainer } from '@calendly/ui/components/fixed-container';
import { CalendlyNotetaker } from '@calendly/ui/components/glyphs/CalendlyNotetaker';
import { IconMaximize } from '@calendly/ui/components/glyphs/IconMaximize';
import { IconSettings } from '@calendly/ui/components/glyphs/IconSettings';
import { IconButton } from '@calendly/ui/components/icon-button';
import { Link } from '@calendly/ui/components/link';
import { ProTip } from '@calendly/ui/components/pro-tip';
import { RadioInput } from '@calendly/ui/components/radio-input';
import { Tooltip } from '@calendly/ui/components/tooltip';
import { BodyText } from '@calendly/ui/components/typography';
import { UiIcon } from '@calendly/ui/components/ui-icon';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { colorTextSecondary, colorTextStandard } from '@calendly/ui/theme';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { getMeetingsFilterMenuPortalRoot } from 'components/meetings/filter_modal/filter_menu_portal_root';
import { meetingsPortaledMenuProTipGlobalCss, portaledMenuShellCss } from 'components/meetings/filter_modal/portaled_menu_styles';
import { AllSchedulingMethods, SchedulingMethods } from 'constants/meetings';
import { useGetUserSettingsQuery, usePatchUserSettingsMutation } from 'features/availability';
import { useFetchCalendarAccountsQuery } from 'features/calendar_connections';
import { SETTINGS_SECTION } from 'features/settings_modal/constants';
import { useSettingsNavigation } from 'features/settings_modal/use_settings_navigation';
import { usePortaledMenuAnchor } from 'hooks/use_portaled_menu_anchor';
import { usePortaledMenuPosition } from 'hooks/use_portaled_menu_position';
import { NOTETAKER_SETTINGS_PATH } from 'shared/constants/personal_browser_urls';
const containerCss = "cmx5wbk";
const Container = forwardRef((props, ref) => <div ref={ref} {...props} className={classNames(containerCss, props.className)} />);
const dropdownMenuCss = "d1ik0hbw";
const headerCss = "h1npfyf2";
const sectionCss = "sobmgb1";

/** Compact preset settings menu: 12px inset at top/sides, 6px above the footer rule. */
const compactSectionCss = "c1qqud6s";
const sectionWithBorderCss = "s9rs0es";
const sectionLabelCss = "sf1034f";
const optionCss = "ouony0b";
const footerCss = "f1bwexi9";

/** Compact preset settings menu: 6px below the rule, 12px at bottom/sides. */
const compactFooterCss = "c7ioiz4";
const footerLinkCss = "fjkv57n";
const compactFooterLinkCss = "ckbfldl";
const footerArrowCss = "fjq5m83";
const settingsButtonCss = "s18ak3ss";
const settingsButtonLabelCss = "s36olw5";
const SettingsSection = ({
  label,
  withBorder = true,
  children
}) => <div className={classNames(sectionCss, {
  [sectionWithBorderCss]: withBorder
})}>
    <div className={sectionLabelCss}>
      <BodyText weight="bold">{label}</BodyText>
    </div>
    {children}
  </div>;
const SchedulingMethodSection = ({
  schedulingMethods,
  onChange
}) => {
  const {
    data: calendarData
  } = useFetchCalendarAccountsQuery();
  const hasOutlookIngestionFlag = useFlag('external_events_ingestion_outlook');
  let pushCalendarName = null;
  if (calendarData?.ids?.length && calendarData.entities) {
    const pushCal = calendarData.ids.find(id => calendarData.entities[id]?.pushCalendarId);
    pushCalendarName = pushCal ? calendarData.entities[pushCal]?.calendarName ?? null : null;
  }
  const isCalendlyOnly = schedulingMethods.length === 1 && schedulingMethods.includes(SchedulingMethods.CALENDLY);
  const isAll = !isCalendlyOnly;
  const onSelect = value => {
    const newMethods = value === SchedulingMethods.ALL ? AllSchedulingMethods : [value];
    onChange(newMethods);
  };
  let connectedCalLabel;
  if (pushCalendarName) {
    connectedCalLabel = I18n.t('publisher.meetings.persistent_settings.show_meetings.all_with_name', {
      name: pushCalendarName
    });
  } else if (hasOutlookIngestionFlag) {
    connectedCalLabel = I18n.t('publisher.meetings.persistent_settings.show_meetings.all_with_outlook');
  } else {
    connectedCalLabel = I18n.t('publisher.meetings.persistent_settings.show_meetings.all');
  }
  return <SettingsSection label={I18n.t('publisher.meetings.persistent_settings.show_meetings.label')} withBorder={false}>
      <label className={optionCss}>
        <RadioInput checked={isAll} onChange={() => onSelect(SchedulingMethods.ALL)} name="persistent-scheduling-method" data-testid="scheduling-method-all" />
        <BodyText>{connectedCalLabel}</BodyText>
      </label>
      <label className={optionCss}>
        <RadioInput checked={isCalendlyOnly} onChange={() => onSelect(SchedulingMethods.CALENDLY)} name="persistent-scheduling-method" data-testid="scheduling-method-calendly" />
        <BodyText>{I18n.t('publisher.meetings.persistent_settings.show_meetings.calendly_only')}</BodyText>
      </label>
    </SettingsSection>;
};
const buffersLabelCss = "b1yrorsk";
const BuffersSection = ({
  showBuffers,
  onToggle,
  compact = false
}) => <div className={classNames(sectionCss, {
  [compactSectionCss]: compact
})}>
    <div className={buffersLabelCss}>
      <BodyText weight="bold">{I18n.t('publisher.meetings.filter_modal.buffers.label')}</BodyText>
      <ProTip>
        {I18n.t('publisher.scheduled_events.buffers.pro_tip_text')}
        {' '}
        <Link rel="noopener noreferrer" target="_blank" underline={false} href="https://calendly.com/help/how-to-use-buffers">
          <BodyText inline weight="bold" color={colorTextSecondary}>{I18n.t('publisher.action.learn_more')}</BodyText>
        </Link>
      </ProTip>
    </div>
    <label className={optionCss}>
      <RadioInput checked={showBuffers} onChange={() => onToggle(true)} name="persistent-buffers" data-testid="buffers-show" />
      <BodyText>{I18n.t('publisher.meetings.persistent_settings.buffers.show')}</BodyText>
    </label>
    <label className={optionCss}>
      <RadioInput checked={!showBuffers} onChange={() => onToggle(false)} name="persistent-buffers" data-testid="buffers-hide" />
      <BodyText>{I18n.t('publisher.meetings.persistent_settings.buffers.hide')}</BodyText>
    </label>
  </div>;
const NotetakerSettingsFooter = ({
  compact = false
}) => {
  const {
    hrefForSettingsModalEntry
  } = useSettingsNavigation();
  return <div className={classNames(footerCss, {
    [compactFooterCss]: compact
  })}>
      <Link as={RouterLink} to={hrefForSettingsModalEntry(SETTINGS_SECTION.ai_notetaker, NOTETAKER_SETTINGS_PATH)} underline={false} data-testid="notetaker-settings-link" className={classNames(footerLinkCss, {
      [compactFooterLinkCss]: compact
    })}>
        <UiIcon icon={CalendlyNotetaker} size="small" color={colorTextStandard} title="" />
        <BodyText color={colorTextStandard}>
          {I18n.t('publisher.meetings.notetaker_settings')}
        </BodyText>
        <span className={footerArrowCss}>
          <UiIcon icon={IconMaximize} size="small" color={colorTextStandard} title="" />
        </span>
      </Link>
    </div>;
};

// TODO(cui-drift): DropdownMenu — even with menuPortalRoot, desktop still portals via RAC
// Popover and triggers ownerDocument.createElementNS errors in the oap_web MFE (#2930, #2938).
// Keep useDropdown + FixedContainer + getMeetingsFilterMenuPortalRoot() until CUI portals
// this surface entirely through createPortal.
export const PersistentFiltersMenu = forwardRef(({
  notetakerEnabled = false,
  iconOnly = false
}, ref) => {
  const [toggleDropdown, Dropdown, isDropdownOpen, {
    dropdownRef
  }] = useDropdown();
  const portalRoot = getMeetingsFilterMenuPortalRoot();
  const {
    anchorRef,
    containerRef
  } = usePortaledMenuAnchor(ref);
  usePortaledMenuPosition(isDropdownOpen, anchorRef, dropdownRef);
  const {
    data: userSettings
  } = useGetUserSettingsQuery();
  const isPhone = useDeviceType('isPhone');
  const isDefaultPresetToggleEnabled = useFlag('sa_default_preset_toggle', false);
  const [updateUserSettings] = usePatchUserSettingsMutation();
  const meetingsPageSettings = userSettings?.meetingsPageSettings || {};
  const showBuffers = meetingsPageSettings.showBuffers ?? true;
  const schedulingMethods = meetingsPageSettings.schedulingMethods?.length > 0 ? meetingsPageSettings.schedulingMethods : AllSchedulingMethods;
  const handleBuffersToggle = newShowBuffers => {
    updateUserSettings({
      user_settings: {
        meetingsPageSettings: {
          showBuffers: newShowBuffers,
          schedulingMethods
        }
      }
    });
  };
  const handleSchedulingMethodChange = newMethods => {
    updateUserSettings({
      user_settings: {
        meetingsPageSettings: {
          schedulingMethods: newMethods,
          showBuffers
        }
      }
    });
  };
  const menu = isDropdownOpen && <Dropdown>
      <FixedContainer ref={dropdownRef} fixedWhen root={portalRoot ?? undefined} className={classNames(portaledMenuShellCss, dropdownMenuCss)} onClick={event => event.stopPropagation()} data-component="filter-dropdown-menu" data-testid="persistent-filters-menu">
        {!isDefaultPresetToggleEnabled && <div className={headerCss}>
            <BodyText weight="bold">
              {I18n.t('publisher.meetings.persistent_settings.title')}
            </BodyText>
          </div>}
        {!isDefaultPresetToggleEnabled && <SchedulingMethodSection schedulingMethods={schedulingMethods} onChange={handleSchedulingMethodChange} />}
        <BuffersSection showBuffers={showBuffers} onToggle={handleBuffersToggle} compact={isDefaultPresetToggleEnabled} />
        {notetakerEnabled && <NotetakerSettingsFooter compact={isDefaultPresetToggleEnabled} />}
      </FixedContainer>
    </Dropdown>;
  const settingsLabel = I18n.t('publisher.meetings.settings');
  return <Container ref={containerRef} className={meetingsPortaledMenuProTipGlobalCss}>
      {iconOnly ? <Tooltip placement="top" text={settingsLabel}>
          <IconButton
      // Figma page-header trailing actions (3250:194468): IconButton medium (40px / 24px glyph).
      size="medium" icon={IconSettings} onClick={toggleDropdown} aria-expanded={isDropdownOpen} aria-label={settingsLabel} data-testid="settings-dropdown-button" />
        </Tooltip> : <BareButton onClick={toggleDropdown} aria-expanded={isDropdownOpen} aria-label={settingsLabel} className={settingsButtonCss} data-testid="settings-dropdown-button">
          <UiIcon icon={IconSettings} size="small" title="" />
          {!isPhone && <span className={settingsButtonLabelCss}>
              <BodyText>{settingsLabel}</BodyText>
            </span>}
        </BareButton>}
      {menu}
    </Container>;
});

require("./persistent_filters_menu.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./persistent_filters_menu.js");